/**
 * Company Structure Mock Data Panel
 *
 * 开发环境下的快捷填充工具
 * 提供可视化的面板用于快速填充公司结构与关联人士数据
 *
 * @module mockDataGenerator.demo
 */

import {
  CompanyRelatedPersonTypeOptions,
  CompanyShareholderTypeOptions,
} from "../../types/enum";
import { Button, Checkbox, InputNumber, Select, message } from "antd";
import { useState } from "react";
import {
  companyStructurePresets,
  generateRelatedPersonData,
  generateRelatedPersonsBatch,
  RelatedPersonData,
} from "./mockDataGenerator.company-structure";

interface CompanyStructureMockDataPanelProps {
  /** 当前关联人士列表 */
  relatedPersons?: RelatedPersonData[];
  /** 设置关联人士列表的回调 */
  onSetRelatedPersons?: (persons: RelatedPersonData[]) => void;
}

/**
 * 公司结构 Mock 数据生成工具面板
 */
// 关联人士类型选项
const RELATED_PERSON_TYPE_OPTIONS = [
  {
    label: "股东/最终实益拥有人",
    value: CompanyRelatedPersonTypeOptions.SHAREHOLDER_BENEFICIAL_OWNER,
  },
  { label: "董事", value: CompanyRelatedPersonTypeOptions.DIRECTOR },
  {
    label: "授权签署人",
    value: CompanyRelatedPersonTypeOptions.AUTHORIZED_SIGNATORY,
  },
  {
    label: "代表客户行事的人",
    value: CompanyRelatedPersonTypeOptions.CLIENT_REPRESENTATIVE,
  },
  { label: "委托人", value: CompanyRelatedPersonTypeOptions.PRINCIPAL },
];

export function CompanyStructureMockDataPanel({
  relatedPersons = [],
  onSetRelatedPersons,
}: CompanyStructureMockDataPanelProps) {
  const [visible, setVisible] = useState(false);
  const [personCount, setPersonCount] = useState(3);
  const [showCustomTypes, setShowCustomTypes] = useState(false);
  const [selectedPersonType, setSelectedPersonType] =
    useState<CompanyShareholderTypeOptions>(
      CompanyShareholderTypeOptions.INDIVIDUAL
    );
  const [selectedRelatedPersonTypes, setSelectedRelatedPersonTypes] = useState<
    CompanyRelatedPersonTypeOptions[]
  >([]);
  const [selectedCorporateTypes, setSelectedCorporateTypes] = useState<
    CompanyRelatedPersonTypeOptions[]
  >([CompanyRelatedPersonTypeOptions.SHAREHOLDER_BENEFICIAL_OWNER]);

  /* ============================================================
   * 批量生成操作
   * ========================================================== */

  const handleGenerateBatch = (count: number) => {
    if (relatedPersons.length + count > 10) {
      message.error("最多生成10个关联人士");
      return;
    }
    const persons = generateRelatedPersonsBatch(count);
    onSetRelatedPersons?.(persons);
    console.log(`✅ 已生成 ${count} 个关联人士:`, persons);
  };

  const handleGenerateCustomCount = () => {
    handleGenerateBatch(personCount);
  };

  /* ============================================================
   * 自定义生成操作
   * ========================================================== */

  const handleAddCustomPerson = () => {
    const person = generateRelatedPersonData({
      type: selectedPersonType,
      relatedPersonTypes:
        selectedPersonType === CompanyShareholderTypeOptions.INDIVIDUAL
          ? selectedRelatedPersonTypes.length > 0
            ? selectedRelatedPersonTypes
            : undefined
          : undefined,
      corporateRelatedPersonTypes:
        selectedPersonType === CompanyShareholderTypeOptions.CORPORATE
          ? selectedCorporateTypes.length > 0
            ? selectedCorporateTypes
            : undefined
          : undefined,
      isShareholdingBeneficiary: selectedRelatedPersonTypes.includes(
        CompanyRelatedPersonTypeOptions.SHAREHOLDER_BENEFICIAL_OWNER
      ),
    });
    onSetRelatedPersons?.([...relatedPersons, person]);
    console.log("✅ 已添加自定义人士:", person);
  };

  /* ============================================================
   * 添加单个操作
   * ========================================================== */

  const handleAddIndividualShareholder = () => {
    if (relatedPersons.length >= 10) {
      message.error("最多生成10个关联人士");
      return;
    }
    const person = companyStructurePresets.individualShareholder();
    onSetRelatedPersons?.([...relatedPersons, person]);
    console.log("✅ 已添加个人股东:", person);
  };

  const handleAddIndividualDirector = () => {
    if (relatedPersons.length >= 10) {
      message.error("最多生成10个关联人士");
      return;
    }
    const person = companyStructurePresets.individualDirector();
    onSetRelatedPersons?.([...relatedPersons, person]);
    console.log("✅ 已添加个人董事:", person);
  };

  const handleAddCorporateShareholder = () => {
    if (relatedPersons.length >= 10) {
      message.error("最多生成10个关联人士");
      return;
    }
    const person = companyStructurePresets.corporateShareholder();
    onSetRelatedPersons?.([...relatedPersons, person]);
    console.log("✅ 已添加法人股东:", person);
  };

  /* ============================================================
   * 预设场景操作
   * ========================================================== */

  const handleFillMixedStructure = () => {
    if (relatedPersons.length >= 10) {
      message.error("最多生成10个关联人士");
      return;
    }
    const persons = companyStructurePresets.mixedStructure();
    onSetRelatedPersons?.(persons);
    console.log("✅ 已填充混合结构（2个个人+1个法人）:", persons);
  };

  const handleFillIndividualOnly = () => {
    if (relatedPersons.length >= 10) {
      message.error("最多生成10个关联人士");
      return;
    }
    const persons = [
      companyStructurePresets.individualShareholder(),
      companyStructurePresets.individualDirector(),
    ];
    onSetRelatedPersons?.(persons);
    console.log("✅ 已填充个人人士结构:", persons);
  };

  const handleFillWithCorporate = () => {
    if (relatedPersons.length >= 10) {
      message.error("最多生成10个关联人士");
      return;
    }
    const persons = [
      companyStructurePresets.individualShareholder(),
      companyStructurePresets.corporateShareholder(),
    ];
    onSetRelatedPersons?.(persons);
    console.log("✅ 已填充个人+法人结构:", persons);
  };

  const handleClear = () => {
    onSetRelatedPersons?.([
      {
        id: `person_${Date.now()}`,
        type: CompanyShareholderTypeOptions.INDIVIDUAL,
        isShareholdingBeneficiary: false,
        data: {},
      },
    ]);
    console.log("✅ 已清空关联人士列表");
  };

  const handleToggle = () => {
    setVisible(!visible);
  };

  return (
    <>
      {/* 浮动按钮 */}
      {!visible && (
        <div
          className="fixed bottom-8 right-8 z-[9999] group cursor-pointer"
          onClick={handleToggle}
          style={{
            width: "64px",
            height: "64px",
          }}
        >
          <div
            className="absolute inset-0 rounded-full transition-all duration-300 group-hover:scale-110"
            style={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              boxShadow:
                "0 10px 25px -5px rgba(102, 126, 234, 0.5), 0 8px 10px -6px rgba(102, 126, 234, 0.3)",
            }}
          />
          <div
            className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"
            style={{
              background: "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
              boxShadow: "0 15px 35px -5px rgba(118, 75, 162, 0.6)",
            }}
          />
          <div className="relative w-full h-full flex items-center justify-center text-3xl transition-transform duration-300 group-hover:scale-110 group-active:scale-95">
            👥
          </div>
          <div
            className="absolute inset-0 rounded-full animate-ping opacity-20"
            style={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              animationDuration: "3s",
            }}
          />
        </div>
      )}

      {/* 主面板 */}
      {visible && (
        <div
          className="fixed bottom-8 right-8 z-[9999] bg-white rounded-lg shadow-2xl"
          style={{
            width: "360px",
            maxHeight: "600px",
            display: "flex",
            flexDirection: "column",
            border: "1px solid #d9d9d9",
          }}
        >
          {/* 标题栏 */}
          <div
            className="flex items-center justify-between px-4 py-3"
            style={{
              borderBottom: "1px solid #f0f0f0",
              backgroundColor: "#fafafa",
              borderRadius: "8px 8px 0 0",
            }}
          >
            <span className="font-semibold text-base">
              👥 公司结构 Mock工具
            </span>
            <Button size="small" onClick={handleToggle}>
              ✕
            </Button>
          </div>

          {/* 内容区域 */}
          <div
            className="overflow-y-auto p-4 space-y-4"
            style={{ maxHeight: "500px" }}
          >
            {/* 当前数量显示 */}
            <div className="text-sm text-gray-600">
              当前关联人士:{" "}
              <span className="font-semibold">{relatedPersons.length}</span> 人
            </div>

            {/* 批量生成区域 */}
            <div className="space-y-2">
              <div className="text-sm font-semibold text-gray-700">
                批量生成
              </div>
              <div className="flex items-center gap-2">
                <InputNumber
                  min={1}
                  max={10}
                  value={personCount}
                  onChange={(value) => setPersonCount(value || 1)}
                  style={{ width: "80px" }}
                  size="small"
                />
                <span className="text-sm text-gray-600">个人士</span>
                <Button
                  size="small"
                  type="primary"
                  onClick={handleGenerateCustomCount}
                  style={{ marginLeft: "auto" }}
                >
                  生成
                </Button>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <Button size="small" onClick={() => handleGenerateBatch(1)}>
                  1人
                </Button>
                <Button size="small" onClick={() => handleGenerateBatch(3)}>
                  3人
                </Button>
                <Button size="small" onClick={() => handleGenerateBatch(5)}>
                  5人
                </Button>
              </div>
            </div>

            {/* 分割线 */}
            <div className="border-t border-gray-200" />

            {/* 自定义生成 */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold text-gray-700">
                  自定义生成
                </div>
                <Button
                  type="link"
                  size="small"
                  onClick={() => setShowCustomTypes(!showCustomTypes)}
                  style={{ padding: 0, height: "auto" }}
                >
                  {showCustomTypes ? "收起" : "展开"}
                </Button>
              </div>
              {showCustomTypes && (
                <div className="space-y-2 bg-gray-50 p-2 rounded">
                  <div>
                    <div className="text-xs text-gray-600 mb-1">人士类型</div>
                    <Select
                      size="small"
                      value={selectedPersonType}
                      onChange={setSelectedPersonType}
                      style={{ width: "100%" }}
                      options={[
                        {
                          label: "个人",
                          value: CompanyShareholderTypeOptions.INDIVIDUAL,
                        },
                        {
                          label: "法人",
                          value: CompanyShareholderTypeOptions.CORPORATE,
                        },
                      ]}
                    />
                  </div>
                  {selectedPersonType ===
                    CompanyShareholderTypeOptions.INDIVIDUAL && (
                    <div>
                      <div className="text-xs text-gray-600 mb-1">
                        关联人士类型
                      </div>
                      <Checkbox.Group
                        value={selectedRelatedPersonTypes}
                        onChange={(values) =>
                          setSelectedRelatedPersonTypes(
                            values as CompanyRelatedPersonTypeOptions[]
                          )
                        }
                        options={RELATED_PERSON_TYPE_OPTIONS}
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "4px",
                        }}
                      />
                    </div>
                  )}
                  {selectedPersonType ===
                    CompanyShareholderTypeOptions.CORPORATE && (
                    <div>
                      <div className="text-xs text-gray-600 mb-1">
                        法人关联类型
                      </div>
                      <Checkbox.Group
                        value={selectedCorporateTypes}
                        onChange={(values) =>
                          setSelectedCorporateTypes(
                            values as CompanyRelatedPersonTypeOptions[]
                          )
                        }
                        options={RELATED_PERSON_TYPE_OPTIONS}
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "4px",
                        }}
                      />
                    </div>
                  )}
                  <Button
                    size="small"
                    type="primary"
                    block
                    onClick={handleAddCustomPerson}
                  >
                    添加自定义人士
                  </Button>
                </div>
              )}
            </div>

            {/* 分割线 */}
            <div className="border-t border-gray-200" />

            {/* 添加单个人士 */}
            <div className="space-y-2">
              <div className="text-sm font-semibold text-gray-700">
                快捷添加
              </div>
              <Button
                size="small"
                block
                onClick={handleAddIndividualShareholder}
              >
                个人股东
              </Button>
              <Button size="small" block onClick={handleAddIndividualDirector}>
                个人董事
              </Button>
              <Button
                size="small"
                block
                onClick={handleAddCorporateShareholder}
              >
                法人股东
              </Button>
            </div>

            {/* 分割线 */}
            <div className="border-t border-gray-200" />

            {/* 预设场景 */}
            <div className="space-y-2">
              <div className="text-sm font-semibold text-gray-700">
                预设场景
              </div>
              <Button size="small" block onClick={handleFillMixedStructure}>
                混合结构（2个人+1法人）
              </Button>
              <Button size="small" block onClick={handleFillIndividualOnly}>
                纯个人结构（2人）
              </Button>
              <Button size="small" block onClick={handleFillWithCorporate}>
                个人+法人（各1）
              </Button>
            </div>
          </div>

          {/* 清空按钮 */}
          <div className="px-4 pb-4 pt-2 border-t">
            <Button danger size="small" block onClick={handleClear}>
              清空列表
            </Button>
          </div>
        </div>
      )}
    </>
  );
}

export default CompanyStructureMockDataPanel;
